import { Alert, Anchor, Col, Flex, Row } from "antd";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import useMedia from "use-media";
import { $t } from "~/i18n.js";
import { BreadcrumbHeader } from "../../components/layout/breadcrumbHeader.jsx";
import { MainLayout } from "../../components/layout/layout.jsx";
import { persistActions } from "../../redux/actions.js";
import { CardBestanden } from "./cards/card.bestanden.jsx";
import { CardBrevet } from "./cards/card.brevet.jsx";
import { CardContact } from "./cards/card.contact.jsx";
import { CardEmergency } from "./cards/card.emergency.jsx";
import { CardLidmaatschap } from "./cards/card.lidmaatschap.jsx";
import { CardMedical } from "./cards/card.medical.jsx";
import { CardPersonalia } from "./cards/card.personalia.jsx";
import { CardPrivacy } from "./cards/card.privacy.jsx";
import { CardSecurity } from "./cards/card.security.jsx";
import { CardGuestClubs } from "./cards/cardGuestClubs.jsx";
import { clubHasFlight } from "~/featureMatrix.jsx";

const DataAnchor = ({ vliegendLid, group_keys }) => {
  const hasFlights = clubHasFlight();
  return (
    <Anchor
      offsetTop={64}
      affix={{
        offsetTop: 64,
      }}
      items={[
        { key: "personalia", href: "#personalia", title: $t("Personalia") },
        hasFlights &&
          vliegendLid && {
            key: "license",
            href: "#license",
            title: $t("Brevet"),
          },
        hasFlights &&
          vliegendLid && {
            key: "medical",
            href: "#medical",
            title: $t("Medical"),
          },
        { key: "bestanden", href: "#bestanden", title: $t("Bestanden") },
        { key: "contact", href: "#contact", title: $t("Contact") },
        { key: "emergency", href: "#emergency", title: $t("Emergency") },
        { key: "membership", href: "#membership", title: $t("Lidmaatschap") },
        hasFlights && { key: "clubs", href: "#clubs", title: $t("Clubs") },
        { key: "privacy", href: "#privacy", title: $t("Privacy") },
        { key: "security", href: "#security", title: $t("Security") },
      ].filter((i) => i)}
    />
  );
};

export const MyDataPagina = ({ history }) => {
  const persist = useSelector((state) => state.persist);
  const { group_name_lookup } = useSelector((state) => state.flights);
  const dispatch = useDispatch();

  const profileAction = async (d) => {
    let res = await dispatch(persistActions.profileAction(d));
    return res;
  };

  const isGastvlieger =
    persist?.profile?.group_keys?.indexOf("gastvlieger") > -1;

  // data is readOnly if guest club has home club as well
  const ReadOnlyGuestUser = isGastvlieger && persist?.profile?.home_club;

  const vliegendLid = persist?.profile?.vliegt;

  const isWide = useMedia({ minWidth: "650px" }, true);
  const hasFlights = clubHasFlight();

  return (
    <MainLayout
      history={history}
      isLoading={persist.isPending || !persist.lastUpdated}
    >
      <BreadcrumbHeader breadcrumbs={[$t("Mijn gegevens")]} />

      <Row>
        <Col span={isWide ? 16 : 24}>
          <Flex className="MyData" gap={15}>
            <div id="personalia">
              <CardPersonalia
                profile={persist.profile}
                gegevens={persist.gegevens}
                saveData={profileAction}
                readOnly={ReadOnlyGuestUser}
              />
            </div>

            {ReadOnlyGuestUser && (
              <Alert
                message={$t(
                  "Als gastvlieger kan je je gegevens wijzigen via je thuisclub"
                )}
                type="warning"
                showIcon
              />
            )}

            {hasFlights && vliegendLid && (
              <div id="license">
                <CardBrevet
                  profile={persist.profile}
                  gegevens={persist.gegevens}
                  saveData={profileAction}
                  club={persist.club}
                  readOnly={ReadOnlyGuestUser}
                />
              </div>
            )}

            {hasFlights && vliegendLid && (
              <div id="medical">
                <CardMedical
                  club={persist.club}
                  profile={persist.profile}
                  gegevens={persist.gegevens}
                  saveData={profileAction}
                  readOnly={ReadOnlyGuestUser}
                />
              </div>
            )}

            <div id="bestanden">
              <CardBestanden
                profile={persist.profile}
                bestanden={persist.bestanden}
              />
            </div>

            <div id="contact">
              <CardContact
                profile={persist.profile}
                gegevens={persist.gegevens}
                saveData={profileAction}
                readOnly={ReadOnlyGuestUser}
              />
            </div>

            <div id="emergency">
              <CardEmergency
                gegevens={persist.gegevens}
                saveData={profileAction}
                readOnly={ReadOnlyGuestUser}
              />
            </div>

            <div id="membership">
              <CardLidmaatschap
                isGastvlieger={isGastvlieger}
                profile={persist.profile}
                club={persist.club}
                saveData={profileAction}
                group_name_lookup={group_name_lookup}
              />
            </div>

            {hasFlights && (
              <div id="clubs">
                <CardGuestClubs
                  isGastvlieger={isGastvlieger}
                  profile={persist.profile}
                  club={persist.club}
                />
              </div>
            )}

            <div id="privacy">
              <CardPrivacy
                profile={persist.profile}
                club={persist.club}
                saveData={profileAction}
                isGastvlieger={isGastvlieger}
              />
            </div>

            <div id="security">
              <CardSecurity
                profile={persist.profile}
                readOnly={ReadOnlyGuestUser}
              />
            </div>
          </Flex>
        </Col>

        {isWide && (
          <Col span={8}>
            <DataAnchor
              vliegendLid={vliegendLid}
              group_keys={persist.profile.group_keys}
            />
          </Col>
        )}
      </Row>
    </MainLayout>
  );
};
